/**
 * Client mirror of the server permission catalog
 * (src/server/auth/permissionKeys.ts). Use with the auth `can()` helper:
 *
 *   const { can } = useAuth();
 *   if (can(PERMISSIONS.payroll.manage)) { ... }
 *
 * Keep in sync with the server constant and the SQL `permissions` table.
 */
export const PERMISSIONS = {
    jobs: { view: "jobs.view", manage: "jobs.manage", schedule: "jobs.schedule" },
    crm: { view: "crm.view", manage: "crm.manage" },
    // A gate code is a credential to the customer's property, so seeing one is
    // its own permission rather than a side effect of crm.view — which is held
    // by roles who never leave the building. #1040.
    access_codes: { view: "access_codes.view" },
    pricing: { view: "pricing.view", manage: "pricing.manage", createRepair: "pricing.create_repair" },
    invoices: { view: "invoices.view", send: "invoices.send" },
    finance: { view: "finance.view", manage: "finance.manage" },
    financials: { view: "financials.view" },
    inventory: { view: "inventory.view", manage: "inventory.manage" },
    payments: { view: "payments.view", manage: "payments.manage" },
    integrations: { view: "integrations.view", manage: "integrations.manage" },
    dispatch: { view: "dispatch.view", manage: "dispatch.manage" },
    officeReview: { view: "office_review.view", manage: "office_review.manage" },
    forms: { manage: "forms.manage" },
    reports: { view: "reports.view" },
    payroll: { view: "payroll.view", manage: "payroll.manage" },
    compliance: { view: "compliance.view", manage: "compliance.manage" },
    hr: { view: "hr.view", manage: "hr.manage" },
    settings: { view: "settings.view", manage: "settings.manage" },
    users: { view: "users.view", manage: "users.manage" },
    roles: { manage: "roles.manage" },
    audit: { view: "audit.view" },
    impersonate: { users: "impersonate.users" },
    photoImport: { use: "photo_import.use" },
    tasks: { view: "tasks.view", manage: "tasks.manage" },
    procurement: { approveVariance: "procurement.approve_variance" },
    support: { flag: "support.flag", diagnose: "support.diagnose", verify: "support.verify" },
} as const;

type PermissionGroup = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
export type PermissionKey = PermissionGroup[keyof PermissionGroup];
